import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CircleCheck,
  CircleHelp,
  Clock3,
  ExternalLink,
  FileCheck2,
  Fingerprint,
  Globe2,
  Info,
  LoaderCircle,
  LockKeyhole,
  Network,
  ScanLine,
  ShieldCheck,
  Wallet,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getAddress } from 'viem';
import { FaucetCard } from '../components/FaucetCard';
import { PageHeading, StatusPill } from '../components/Ui';
import { isMemberId, shortenAddress } from '../lib/format';
import { explorerUrlFor } from '../lib/network';
import { formatJoinAmount,
  loadJoinQuote,
  normalizeJoinError,
  resolveProtocolConfiguration,
  submitJoin,
  type JoinFlowError,
  type JoinProgress,
  type JoinQuote,
  type JoinResult, uintToMemberId } from '../lib/protocol';
import { useWallet } from '../state/WalletContext';
import { useAppData } from '../state/AppDataContext';
import { useCdtBenefitParams } from '../components/CdtBenefitCard';
import { describeCdtBenefitError, loadRatesBundle, normalizeCode, ratesBundleUsable, readOracleUpdateDue, sendSelectCountry, sendSelectCountryWithRates, type CdtRatesBundle } from '../lib/cdt-benefit';
import { compareCountries, countryLabel, countryMatches } from '../lib/countryNames';

const steps = ['Sponsor', 'Review', 'Authorize'] as const;
const eraDetails = [
  { name: 'Standard', multiplier: '1.00×', startingSlot: 'Slot 1' },
  // Rule 16 Section I: Half / Quarter IDs enter the sub-slot below Slot 1 (contract keys 50 / 25).
  { name: 'Half', multiplier: '0.50×', startingSlot: 'Slot ½' },
  { name: 'Quarter', multiplier: '0.25×', startingSlot: 'Slot ¼' },
] as const;

function progressLabel(progress: JoinProgress | null, kind: 'approval' | 'join' | 'finality'): string {
  if (!progress) return kind === 'approval' ? 'Ready' : 'Queued';
  if (kind === 'approval') {
    if (progress.stage === 'approval-skipped') return 'Already exact';
    if (progress.stage === 'approval-finalized' || progress.stage.startsWith('join-')) return 'Finalized';
    if (progress.stage.startsWith('approval-')) return progress.stage === 'approval-submitted' ? 'In wallet' : 'Confirming';
    return 'Ready';
  }
  if (kind === 'join') {
    if (progress.stage === 'join-finalized') return 'Finalized';
    if (progress.stage.startsWith('join-')) return progress.stage === 'join-submitted' ? 'In wallet' : 'Confirming';
    return 'Queued';
  }
  return progress.stage === 'join-finalized' ? 'Finalized' : progress.stage.startsWith('join-') ? 'Waiting' : 'Queued';
}

export function JoinPage() {
  const wallet = useWallet();
  const data = useAppData();
  const [searchParams] = useSearchParams();
  const requestedSponsor = searchParams.get('ref')?.trim().toUpperCase();
  const availability = useMemo(() => resolveProtocolConfiguration(), []);
  const [step, setStep] = useState(0);
  const [sponsor, setSponsor] = useState(() => requestedSponsor && isMemberId(requestedSponsor) ? requestedSponsor : '');
  const [touched, setTouched] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState<JoinQuote | null>(null);
  const [progress, setProgress] = useState<JoinProgress | null>(null);
  const [flowError, setFlowError] = useState<JoinFlowError | null>(null);
  const [result, setResult] = useState<JoinResult | null>(null);
  // docs/CDT_BENEFIT_SPEC.md s.2: the country list is the contract's (countries() via the live params); required when the
  // block exists; after the join finalizes the same wallet sends selectCountry(id, country) as a second transaction.
  const cdtBenefit = useCdtBenefitParams();
  const countryOptions = useMemo(() => (cdtBenefit?.countries ?? []).map((row) => ({ code: normalizeCode(row.country), currency: normalizeCode(row.currency) })).filter((row) => row.code).sort((a, b) => compareCountries(a.code, b.code)), [cdtBenefit]);
  const countryRequired = Boolean(cdtBenefit?.address) && countryOptions.length > 0;
  const [country, setCountry] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const visibleCountryOptions = useMemo(() => countryOptions.filter((row) => row.code === country || countryMatches(row.code, row.currency, countrySearch)), [countryOptions, country, countrySearch]);
  const [countryTouched, setCountryTouched] = useState(false);
  const [countryHash, setCountryHash] = useState<string | null>(null);
  const [countryError, setCountryError] = useState<string | null>(null);
  const [countryBusy, setCountryBusy] = useState(false);
  const countryValid = !countryRequired || countryOptions.some((row) => row.code === country);
  // Lazy oracle, client decision 2026-11-01: NO separate "update rates" click. When the API's sparse bundle says an update
  // is due AND the live contract agrees (oracleUpdateDue(); a contract without the function = not due), the country
  // transaction itself is selectCountryWithRates(id, country, <bundle>) - the contract applies the rates best effort and
  // records the country in the same confirmation. Otherwise it is the plain selectCountry. A bundle fetch failure never
  // blocks the registration. `countryBundle` is the bundle that rode along (for the note under the confirmation).
  const [countryBundle, setCountryBundle] = useState<CdtRatesBundle | null>(null);
  const ratesWindowHours = countryBundle?.window ? Math.max(1, Math.round(countryBundle.window / 3600)) : 6;
  const sponsorValid = isMemberId(sponsor);
  const wrongNetwork = Boolean(wallet.address && wallet.chainId !== wallet.expectedChainId);
  const busy = validating || submitting;
  const chainId: 56 | 97 = wallet.expectedChainId === 56 ? 56 : 97;
  const era = eraDetails[quote?.era ?? 0];
  // The entry amount is only ever the contract's quote; until it is read the page says so (never a constant).
  const entryAmount = quote ? formatJoinAmount(quote.entryAmount, quote.tokenDecimals) : null;
  const entryAmountText = entryAmount === null ? 'loading from the contract…' : `${entryAmount} BTCB`;

  useEffect(() => {
    if (!touched && !requestedSponsor && data.member?.id) setSponsor(data.member.id);
  }, [data.member?.id, requestedSponsor, touched]);

  const resetFlow = () => {
    setQuote(null);
    setProgress(null);
    setFlowError(null);
    setResult(null);
  };

  const actionLabel = useMemo(() => {
    if (!wallet.address) return wallet.connecting ? 'Connecting…' : 'Connect wallet';
    if (wrongNetwork) return wallet.expectedChainId === 56 ? 'Switch to BNB Mainnet' : 'Switch to BNB Testnet';
    if (validating) return 'Validating finalized sponsor…';
    if (step < 2) return step === 0 ? 'Review entry' : 'Continue to authorization';
    if (!availability.configured) return 'Awaiting testnet deployment';
    if (submitting) {
      if (!progress || progress.stage.startsWith('approval-')) return 'Finalizing exact approval…';
      return progress.stage === 'join-finalized' ? 'Finalized' : 'Finalizing B-Dynamic ID…';
    }
    if (result) return `${result.memberId} created`;
    if (flowError) return 'Retry safely';
    return entryAmount === null ? 'Loading the entry amount…' : `Authorize ${entryAmount} BTCB`;
  }, [availability.configured, entryAmount, flowError, progress, result, step, submitting, validating, wallet.address, wallet.connecting, wrongNetwork]);

  const validateSponsor = async (): Promise<boolean> => {
    setTouched(true);
    setFlowError(null);
    if (!sponsorValid) return false;
    if (!availability.configured || !wallet.address) return true;

    setValidating(true);
    try {
      const nextQuote = await loadJoinQuote(availability.config, getAddress(wallet.address), sponsor);
      setQuote(nextQuote);
      return true;
    } catch (cause) {
      setFlowError(normalizeJoinError(cause));
      setQuote(null);
      return false;
    } finally {
      setValidating(false);
    }
  };

  const executeJoin = async () => {
    if (!availability.configured || !wallet.address || !quote) return;
    // If the previous attempt had already broadcast the join, continue from that hash. Captured
    // BEFORE progress is cleared, and only for join-stage hashes - an approval hash must not be
    // mistaken for a join.
    const resumeFrom = flowError && progress && progress.stage.startsWith('join-') && (progress.hash || progress.fromBlock !== undefined)
      ? { ...(progress.hash ? { joinHash: progress.hash } : {}), ...(progress.fromBlock !== undefined ? { fromBlock: progress.fromBlock } : {}) }
      : undefined;
    setSubmitting(true);
    setFlowError(null);
    setProgress(null);
    setResult(null);
    try {
      const account = getAddress(wallet.address);
      if (resumeFrom) {
        const joined = await submitJoin(availability.config, account, quote, setProgress, resumeFrom);
        setResult(joined);
        return;
      }
      const freshQuote = await loadJoinQuote(availability.config, account, sponsor);
      setQuote(freshQuote);
      const joined = await submitJoin(availability.config, account, freshQuote, setProgress);
      setResult(joined);
    } catch (cause) {
      const error = normalizeJoinError(cause);
      setFlowError(error);
      if (error.code === 'invalid-sponsor') {
        setStep(0);
        setQuote(null);
      } else if (error.code === 'quote-changed') {
        setStep(1);
        setAccepted(false);
        setQuote(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  /** The bundle the country transaction carries: the API's sparse bundle (due, fresh, the member's currency first) with the live contract agreeing; null = plain selectCountry. */
  const resolveCountryBundle = async (contract: string): Promise<CdtRatesBundle | null> => {
    const currency = countryOptions.find((row) => row.code === country)?.currency ?? null;
    const bundle = await loadRatesBundle({ for: 'join', currency });
    if (!ratesBundleUsable(bundle)) return null;
    const live = await readOracleUpdateDue(chainId, contract);
    return live?.due ? bundle : null;
  };

  const sendCountry = async (memberId: string) => {
    if (!countryRequired || !wallet.address || !cdtBenefit?.address) return;
    setCountryBusy(true);
    setCountryError(null);
    try {
      const account = getAddress(wallet.address);
      const bundle = await resolveCountryBundle(cdtBenefit.address);
      const hash = bundle
        ? await sendSelectCountryWithRates(chainId, account, cdtBenefit.address, memberId, country, bundle)
        : await sendSelectCountry(chainId, account, cdtBenefit.address, memberId, country);
      setCountryBundle(bundle);
      setCountryHash(hash);
    } catch (cause) {
      setCountryError(describeCdtBenefitError(cause, 'The country selection could not be sent.'));
    } finally {
      setCountryBusy(false);
    }
  };

  useEffect(() => {
    if (result && countryRequired && !countryHash && !countryBusy && !countryError) void sendCountry(result.memberId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const handlePrimary = async () => {
    if (!wallet.address) {
      await wallet.connect();
      return;
    }
    if (wrongNetwork) {
      await wallet.switchNetwork();
      return;
    }
    if (step === 0) {
      setCountryTouched(true);
      if (!countryValid) return;
      if (await validateSponsor()) setStep(1);
      return;
    }
    if (step === 1 && accepted) {
      setFlowError(null);
      setStep(2);
      return;
    }
    if (step === 2) await executeJoin();
  };

  const approvalState = progressLabel(progress, 'approval');
  const joinState = progressLabel(progress, 'join');
  const finalityState = progressLabel(progress, 'finality');
  const explorerUrl = availability.configured ? availability.config.explorerUrl : explorerUrlFor(wallet.expectedChainId);

  return (
    <>
      <PageHeading
        eyebrow="ON-CHAIN ENTRY"
        title="Create a B-Dynamic ID"
        description="One transparent transaction. No account password, personal profile, or custodial key."
        actions={<StatusPill tone="green"><ShieldCheck size={13} /> Exact-allowance flow</StatusPill>}
      />

      <div className="join-layout">
        <section className="panel join-panel">
          <div className="stepper" aria-label="Join progress">
            {steps.map((label, index) => <div key={label} className={`${index === step ? 'active' : ''} ${index < step ? 'complete' : ''}`}><span>{index < step ? <Check size={14} /> : index + 1}</span><strong>{label}</strong><i /></div>)}
          </div>

          <AnimatePresence mode="wait">
            {step === 0 ? (
              <motion.div key="sponsor" className="join-stage" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="stage-icon"><Fingerprint /></div>
                <span className="eyebrow">STEP 01</span>
                <h2>Confirm your sponsor</h2>
                <p>Your sponsor becomes an immutable edge in the generation tree after the transaction is finalized.</p>
                <label className="field-label" htmlFor="sponsor">B-Dynamic sponsor ID <button type="button" title="IDs are sequential: BD-1, BD-2, BD-3 and so on"><CircleHelp size={14} /></button></label>
                <div className={`input-shell ${touched && !sponsorValid ? 'invalid' : ''}`}>
                  <ScanLine size={19} />
                  <input
                    id="sponsor"
                    value={sponsor}
                    maxLength={12}
                    autoComplete="off"
                    spellCheck={false}
                    disabled={busy}
                    onChange={(event) => {
                      setSponsor(event.target.value.toUpperCase().replace(/\s/g, ''));
                      setTouched(true);
                      resetFlow();
                    }}
                    placeholder="BD-1"
                  />
                  {validating ? <LoaderCircle className="input-valid spin" size={19} /> : sponsorValid ? <CircleCheck className="input-valid" size={19} /> : null}
                </div>
                {touched && !sponsorValid ? (
                  <span className="field-error">Enter a valid B-Dynamic ID such as BD-1.</span>
                ) : (
                  <span className="field-hint"><LockKeyhole size={13} /> {requestedSponsor === sponsor ? 'Referral link applied. ' : ''}{availability.configured ? 'The ID will be checked at the configured finality depth.' : 'Format preview only until testnet contracts are configured.'}</span>
                )}
                {countryRequired ? (
                  <>
                    <label className="field-label" htmlFor="join-country" style={{ marginTop: 14 }}>Your country <button type="button" title="Rule 43: your country sets your 4x CDT target at activation (rate locked). Stored on the CDT Benefit contract after the join; the CDT accrues as a locked balance from your join and you settle once at maturity."><CircleHelp size={14} /></button></label>
                    <input className="country-search" type="search" aria-label="Search country" placeholder="Type to search: India, Dubai, USA, UK..." value={countrySearch} disabled={busy} onChange={(event) => setCountrySearch(event.target.value)} style={{ marginBottom: 8, width: '100%' }} />
                    {countrySearch && visibleCountryOptions.length === 0 ? <span className="field-hint">No country matches that search. Try the English name, the 2-letter code or a city (Dubai = United Arab Emirates).</span> : null}
                    <div className={`input-shell join-country ${countryTouched && !countryValid ? 'invalid' : ''}`}>
                      <Globe2 size={19} />
                      <select id="join-country" value={country} disabled={busy} onChange={(event) => { setCountry(event.target.value); setCountryTouched(true); }}>
                        <option value="">Select your country…</option>
                        {visibleCountryOptions.map((row) => <option key={row.code} value={row.code}>{countryLabel(row.code, row.currency)}</option>)}
                      </select>
                      {countryValid && country ? <CircleCheck className="input-valid" size={19} /> : null}
                    </div>
                    {countryTouched && !countryValid ? <span className="field-error">Select the country whose currency your CDT Benefit is paid in.</span> : <span className="field-hint"><Globe2 size={13} /> Your country sets your 4x CDT target at activation (rate locked). Sent to the CDT Benefit contract right after your ID is created (second transaction, gas only).</span>}
                  </>
                ) : null}
                <div className="sponsor-preview">
                  <span className="mini-avatar">BD</span>
                  <div>
                    <small>SPONSOR RECORD</small>
                    <strong>{sponsorValid ? sponsor : 'Waiting for a valid ID'}</strong>
                    <span>{quote ? `Finalized at block ${quote.confirmedBlock.toString()} · ${shortenAddress(quote.sponsorWallet, 5)}` : availability.configured ? 'Finalized validation pending' : 'Contract validation unavailable'}</span>
                  </div>
                  {quote ? <StatusPill tone="green">Confirmed</StatusPill> : null}
                </div>
                {flowError ? <div className="transaction-callout error" role="alert"><AlertTriangle /><div><strong>Sponsor validation stopped</strong><p>{flowError.message}</p></div></div> : null}
              </motion.div>
            ) : null}

            {step === 1 ? (
              <motion.div key="review" className="join-stage" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="stage-icon"><FileCheck2 /></div>
                <span className="eyebrow">STEP 02</span>
                <h2>Review exact entry</h2>
                <p>Values are resolved again before submission. The contract rejects overpayment, underpayment, a frozen transition, or stale era data.</p>
                {quote && quote.currentIdCount > 0 ? (
                  <div className="transaction-callout warning" role="status">
                    <AlertTriangle />
                    <div>
                      <strong>This wallet already holds {quote.currentIdCount === 1 ? 'a B-Dynamic ID' : `${quote.currentIdCount} B-Dynamic IDs`}: {quote.existingIds.map((id) => uintToMemberId(id)).join(', ')}</strong>
                      <p>Read from the contract just now. If your earlier join looked like it failed, it did not - the ID above is yours. Continuing here creates an <em>additional</em> ID for this wallet and pays another entry.</p>
                    </div>
                  </div>
                ) : null}
                <div className="entry-receipt">
                  <div><span>Entry era</span><strong>{era.name} <StatusPill tone="violet">{era.multiplier}</StatusPill></strong></div>
                  <div><span>Starting position</span><strong>{era.startingSlot}</strong></div>
                  <div><span>Sponsor</span><strong>{sponsor}</strong></div>
                  {countryRequired ? <div><span>Country (CDT Benefit)</span><strong>{country} · {countryOptions.find((row) => row.code === country)?.currency ?? ''}</strong></div> : null}
                  <div><span>Payment asset</span><strong><i className="coin-dot">₿</i> BTCB</strong></div>
                  {quote ? <div><span>Finalized quote block</span><strong>{quote.confirmedBlock.toString()}</strong></div> : null}
                  <hr />
                  <div className="receipt-total"><span>Exact contract amount<small>Network gas is separate</small></span><strong>{entryAmount === null ? 'Loading…' : <>{entryAmount} <em>BTCB</em></>}</strong></div>
                </div>
                {!quote ? <div className="safety-callout"><Info /><div><strong>Deployment preview</strong><p>The entry amount appears once it is read from the contract. No transaction can be submitted until verified testnet addresses are configured and the quote is finalized.</p></div></div> : null}
                <label className="check-line"><input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} /><span><i><Check size={13} /></i></span><p>I understand that blockchain transactions are public and irreversible, and I have independently reviewed the configured contract.</p></label>
              </motion.div>
            ) : null}

            {step === 2 ? (
              <motion.div key="authorize" className="join-stage" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="stage-icon"><Wallet /></div>
                <span className="eyebrow">STEP 03</span>
                <h2>Authorize from your wallet</h2>
                <p>The flow sets the allowance to exactly {entryAmountText}, submits the join only after approval finality, and waits for {availability.configured ? availability.config.confirmations : 5} confirmations.</p>
                <div className="authorization-flow" aria-live="polite">
                  <div className={approvalState === 'Finalized' || approvalState === 'Already exact' ? 'complete' : progress?.stage.startsWith('approval-') ? 'active' : availability.configured ? 'ready' : ''}><span>1</span><div><strong>Approve exact amount</strong><small>{entryAmount === null ? 'amount loading…' : `${entryAmount} BTCB only`}</small></div><i>{approvalState}</i></div>
                  <ArrowRight />
                  <div className={joinState === 'Finalized' ? 'complete' : progress?.stage.startsWith('join-') ? 'active' : ''}><span>2</span><div><strong>Create B-Dynamic ID</strong><small>Sponsor {sponsor}</small></div><i>{joinState}</i></div>
                  <ArrowRight />
                  <div className={finalityState === 'Finalized' ? 'complete' : progress?.stage.startsWith('join-') ? 'active' : ''}><span>3</span><div><strong>Confirm finality</strong><small>Decode IdCreated event</small></div><i>{finalityState}</i></div>
                </div>

                {!availability.configured ? <div className="safety-callout"><ShieldCheck /><div><strong>Transaction lock is active</strong><p>{availability.reason} The interface will not guess an address or request funds.</p></div></div> : null}
                {submitting ? <div className="transaction-callout pending" role="status"><LoaderCircle className="spin" /><div><strong>{actionLabel}</strong><p>Keep this page open. A replacement transaction will be tracked automatically.</p>{progress?.hash ? <a href={`${explorerUrl}/tx/${progress.hash}`} target="_blank" rel="noreferrer">View current transaction <ExternalLink /></a> : null}</div></div> : null}
                {flowError ? (
                  <div className="transaction-callout error" role="alert">
                    <AlertTriangle />
                    <div>
                      <strong>Transaction stopped safely</strong>
                      <p>{flowError.message}</p>
                      {progress && (progress.hash || progress.fromBlock !== undefined) && progress.stage.startsWith('join-') ? (
                        <p className="callout-note">Your join transaction was already sent. Retry will check it and continue - it will not be sent twice.</p>
                      ) : null}
                      {progress?.hash ? <a href={`${explorerUrl}/tx/${progress.hash}`} target="_blank" rel="noreferrer">Check the last transaction <ExternalLink /></a> : null}
                      {flowError.detail ? <small className="callout-detail" title="Raw wallet / RPC response">{flowError.detail}</small> : null}
                    </div>
                  </div>
                ) : null}
                {flowError && /insufficient|BTCB|gas|BNB|funds/i.test(flowError.message) ? <FaucetCard compact /> : null}
                {result ? <div className="transaction-callout success" role="status"><CircleCheck /><div><strong>{result.memberId} is finalized</strong><p>Created in block {result.blockNumber.toString()} with sponsor {sponsor}.</p><a href={`${explorerUrl}/tx/${result.joinHash}`} target="_blank" rel="noreferrer">View finalized join <ExternalLink /></a></div></div> : null}
                {result && countryRequired ? (
                  <div className={`transaction-callout ${countryHash ? 'success' : countryError ? 'error' : 'pending'}`} role="status" data-testid="join-country-result">
                    {countryHash ? <CircleCheck /> : countryError ? <AlertTriangle /> : <LoaderCircle className="spin" />}
                    <div>
                      <strong>{countryHash ? `Country ${country} recorded for ${result.memberId}` : countryError ? 'Country selection not sent yet' : `Confirm the country selection (${country}) in your wallet…`}</strong>
                      <p>{countryHash ? `${countryBundle ? 'selectCountryWithRates' : 'selectCountry'} sent from the same wallet to the CDT Benefit contract.` : countryError ?? 'Second transaction: selectCountry(id, country) on the CDT Benefit contract. Gas only.'}</p>
                      {countryHash && countryBundle ? <p className="callout-note" data-testid="join-country-rates-note">Exchange rates were refreshed in this transaction ({ratesWindowHours}-hour oracle).</p> : null}
                      {countryHash ? <a href={`${explorerUrl}/tx/${countryHash}`} target="_blank" rel="noreferrer">View country transaction <ExternalLink /></a> : null}
                      {countryError ? <button className="button secondary small" type="button" disabled={countryBusy} onClick={() => void sendCountry(result.memberId)}>Retry country selection</button> : null}
                    </div>
                  </div>
                ) : null}
              </motion.div>
            ) : null}
          </AnimatePresence>

          <div className="join-actions">
            {step > 0 ? (
              <button className="button ghost" type="button" disabled={busy} onClick={() => { setFlowError(null); setProgress(null); setStep((value) => Math.max(0, value - 1)); }}><ArrowLeft size={16} /> Back</button>
            ) : <Link className="button ghost" to="/">Cancel</Link>}
            <button
              className="button primary"
              type="button"
              onClick={() => void handlePrimary()}
              disabled={busy || Boolean(result) || (step === 0 && touched && !sponsorValid) || (step === 1 && !accepted) || (step === 2 && !availability.configured)}
            >
              {busy ? <LoaderCircle className="spin" size={16} /> : null}{actionLabel}{!busy ? <ArrowRight size={16} /> : null}
            </button>
          </div>
        </section>

        <aside className="join-aside">
          <article className="panel era-summary-card">
            <div className="mini-orbit"><span /><i /><b /></div>
            <span className="eyebrow">{quote ? 'FINALIZED ERA' : 'ENTRY PREVIEW'}</span><h3>{era.name}</h3><strong>{era.multiplier} economics</strong>
            <div className="era-detail"><span><Clock3 /> Quote finality</span><strong>{quote ? `${availability.configured ? availability.config.confirmations : 5} blocks` : 'Not loaded'}</strong></div>
            <div className="era-detail"><span><Network /> Target chain</span><strong>{wallet.expectedChainId === 56 ? 'BSC Mainnet' : 'BSC Testnet'}</strong></div>
          </article>
          <article className="panel protection-list">
            <h3><ShieldCheck /> Built-in protections</h3>
            <ul>
              <li><span><Check /></span><p><strong>Exact amount</strong>Fixed-point base units; no floating math.</p></li>
              <li><span><Check /></span><p><strong>Bounded approval</strong>The requested allowance equals one entry only.</p></li>
              <li><span><Check /></span><p><strong>Wrong-network guard</strong>No mutation outside the configured chain.</p></li>
              <li><span><Check /></span><p><strong>Replacement aware</strong>Repriced and replaced transactions retain finality tracking.</p></li>
            </ul>
          </article>
          <FaucetCard />
          <div className="testnet-notice"><Info /><p><strong>Testnet first</strong>This build uses tBNB for gas and a mock BTCB-compatible token until production review.</p></div>
        </aside>
      </div>
    </>
  );
}
